import React, { useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const logo = `${process.env.PUBLIC_URL}/logo-original.png`;

const APP_SCHEME       = 'shiduchimapp';
const ANDROID_PACKAGE  = 'com.zivugmishamayim';
const IOS_APP_STORE_ID = '6760430020';
const APP_STORE_URL    = `https://apps.apple.com/app/id${IOS_APP_STORE_ID}`;
const PLAY_STORE_URL   = 'https://play.google.com/store/apps/details?id=com.zivugmishamayim';

const IOS_STORE_FALLBACK_MS = 2000;

/** Web pathname /invite or /invite/foo -> intent path invite or invite/foo (matches profile deep link style). */
function inviteDeepPath(pathname, search) {
  const tail = pathname.replace(/^\/invite\/?/, '');
  const base = tail ? `invite/${tail}` : 'invite';
  return `${base}${search}`;
}

function InvitePage() {
  const location = useLocation();
  const iosStoreFallbackTimerRef = useRef(null);

  const openApp = useCallback(() => {
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isIOS     = /iPhone|iPad|iPod/i.test(ua);
    const pathForIntent = inviteDeepPath(location.pathname, location.search);

    if (isAndroid) {
      window.location.href =
        `intent://${pathForIntent}#Intent;scheme=${APP_SCHEME};` +
        `package=${ANDROID_PACKAGE};` +
        `S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`;
      return;
    }

    if (iosStoreFallbackTimerRef.current != null) {
      clearTimeout(iosStoreFallbackTimerRef.current);
      iosStoreFallbackTimerRef.current = null;
    }

    const schemeUrl = `${APP_SCHEME}://${pathForIntent}${location.hash || ''}`;
    const started = Date.now();
    window.location.href = schemeUrl;

    if (isIOS) {
      iosStoreFallbackTimerRef.current = setTimeout(() => {
        iosStoreFallbackTimerRef.current = null;
        if (Date.now() - started < IOS_STORE_FALLBACK_MS + 200 && !document.hidden) {
          window.location.href = APP_STORE_URL;
        }
      }, IOS_STORE_FALLBACK_MS);
    }
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
    if (!isMobile) return;
    const t = setTimeout(openApp, 150);
    return () => {
      clearTimeout(t);
      if (iosStoreFallbackTimerRef.current != null) {
        clearTimeout(iosStoreFallbackTimerRef.current);
        iosStoreFallbackTimerRef.current = null;
      }
    };
  }, [openApp]);

  return (
    <div className="App share-page">
      <nav className="navbar">
        <div className="container">
          <div className="nav-content">
            <div className="logo">
              <Link to="/" aria-label="Zivug Mishamayim home">
                <img src={logo} alt="Zivug Mishamayim" className="logo-image" />
              </Link>
            </div>
            <div className="nav-links">
              <Link to="/">Home</Link>
              <Link to="/donate">Donate</Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="share-landing">
        <div className="share-card">
          <img src={logo} alt="Zivug Mishamayim" className="share-logo" />

          <h1 className="share-title">You're invited to Zivug Mishamayim</h1>
          <p className="share-subtitle">
            Open the app to continue. If nothing happens, you can download it below.
          </p>
          <button type="button" className="share-btn share-btn-primary" onClick={openApp}>
            Open in App
          </button>

          <div className="share-divider">
            <span>Don't have the app yet?</span>
          </div>

          <div className="share-store-buttons">
            <button
              className="share-btn share-btn-store"
              disabled
              style={{ opacity: 0.6, cursor: 'not-allowed' }}
            >
              App Store - Coming Soon
            </button>
            <a
              href="https://play.google.com/store/apps/details?id=com.zivugmishamayim&pli=1"
              className="share-btn share-btn-store"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get it on Google Play
            </a>
          </div>

          <p className="share-footer-note">
            <Link to="/support">Support</Link>
            {' · '}
            <Link to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default InvitePage;
